import {
    ContractPartSource,
    DocumentSource,
    FOLDER_TOKEN_PREFIX,
    isAreaToken,
    isContractPartSource,
    isFilterCategorySource,
    isFolderToken,
    SourceToken,
} from "../shared/marvinSources";
import { ApiDocumentTypes, documentTypeLabel } from "../shared/interfaces";
import { DOCUMENTS_CATEGORY_SCOPE, searchScopeLabel } from "../shared/searchScopes";
import { documentSourceLabel } from "../shared/marvinSources";

/** Human label for one `documentFilters` token — folder tokens resolve to the
 *  folder's name (falling back to "Folder" when it no longer exists), the
 *  contract scope, the Models source and document types to their display names. */
export function documentFilterLabel(token: string, folderNameById: Map<string, string>): string {
    if (isWireSource(token)) return documentSourceLabel(token);
    if (token === DOCUMENTS_CATEGORY_SCOPE) return "Documents";
    if (isFilterCategorySource(token)) return searchScopeLabel(token);
    if (token.startsWith("folder:")) {
        return folderNameById.get(token.slice("folder:".length)) ?? "Folder";
    }
    return documentTypeLabel(token);
}

/** Sources whose selector token is also their `documentFilters` token: the contract scope, its
 *  parts and the Models source. */
function isWireSource(
    token: string,
): token is DocumentSource.ContractDocs | DocumentSource.Models | ContractPartSource {
    return (
        token === DocumentSource.ContractDocs ||
        token === DocumentSource.Models ||
        isContractPartSource(token)
    );
}

/**
 * Maps a Marvin source-selector token to the `documentFilters` tokens sent on
 * conversation create/send (`POST /api/ai/conversation`, the Run API). The
 * server parses these directly: plain document-type names are unchanged;
 * `"contract-docs"` expands server-side to the contract scope plus
 * Documents-layer docs marked as contract documents, and each
 * `"contract-docs:<part>"` narrows that scope to one part; `"category:documents"`
 * covers the full Documents library; `"folder:<uuid>"` expands to that
 * folder's subtree; and `"area:<uuid>"` narrows the rest to that area.
 * `"models"` reaches the project's 3D models and no document.
 */
export function getDocumentFilters(source: SourceToken): string[] {
    if (source === DocumentSource.Documents) {
        return [DOCUMENTS_CATEGORY_SCOPE];
    }
    if (isWireSource(source)) {
        return [source];
    }
    if (isFolderToken(source) || isAreaToken(source)) {
        return [source];
    }
    if (isFilterCategorySource(source)) {
        return [source];
    }

    switch (source) {
        case DocumentSource.Codes:
            return [ApiDocumentTypes.Code];
        case DocumentSource.Contracts:
            return [ApiDocumentTypes.Contract];
        case DocumentSource.IFOA:
            return [ApiDocumentTypes.IFOA];
        case DocumentSource.PrimeContract:
            return [ApiDocumentTypes.PrimeContract];
        case DocumentSource.Submittals:
            return [ApiDocumentTypes.Submittal];
    }
}

/**
 * Union of `getDocumentFilters` across every selected source, de-duped — what
 * a multi-source Marvin source selector sends as `documentFilters`.
 */
export function getDocumentFiltersForSources(sources: SourceToken[]): string[] {
    return Array.from(new Set(sources.flatMap((source) => getDocumentFilters(source))));
}

const TYPE_TOKEN_TO_SOURCE: Record<string, DocumentSource> = {
    [ApiDocumentTypes.Code]: DocumentSource.Codes,
    [ApiDocumentTypes.Contract]: DocumentSource.Contracts,
    [ApiDocumentTypes.IFOA]: DocumentSource.IFOA,
    [ApiDocumentTypes.PrimeContract]: DocumentSource.PrimeContract,
    [ApiDocumentTypes.Submittal]: DocumentSource.Submittals,
};

/** The pre-Document-Management-v2 contract-scope type list. Kept only to
 *  recognize old persisted conversations' `documentFilters` in
 *  `sourcesFromDocumentFilters` below — new conversations always generate the
 *  single "contract-docs" token via `getDocumentFilters`. */
function legacyContractScopeTypes(shopDrawingsEnabled: boolean): string[] {
    return [
        ApiDocumentTypes.Drawing,
        ApiDocumentTypes.CSI,
        ApiDocumentTypes.AzDOT,
        ApiDocumentTypes.TxDOT,
        ApiDocumentTypes.RFI,
        ...(shopDrawingsEnabled ? [ApiDocumentTypes.ShopDrawing] : []),
        ApiDocumentTypes.Schedule,
        ApiDocumentTypes.Supplemental,
        ApiDocumentTypes.Generic,
        ApiDocumentTypes.FinishSpec,
    ];
}

/**
 * Inverse of `getDocumentFiltersForSources`: recover the source-selector
 * selection from a conversation's persisted `documentFilters`. `[]` maps to an
 * empty selection (unscoped). Returns null when the stored tokens are not a
 * union this vocabulary can produce (a caller then falls back to a read-only
 * rendering). The legacy pre-Document-Management-v2 contract expansion is
 * still recognized here so conversations created before that rollout keep
 * mapping back to the ContractDocs source.
 */
export function sourcesFromDocumentFilters(documentFilters: string[]): SourceToken[] | null {
    const remaining = new Set(documentFilters);
    const sources: SourceToken[] = [];

    if (remaining.delete("contract-docs")) {
        sources.push(DocumentSource.ContractDocs);
    } else {
        // Widest variant first: the plain list is a subset of the ShopDrawing
        // one, so matching it first would strand the ShopDrawing token.
        for (const shopDrawingsEnabled of [true, false]) {
            const legacyScope = legacyContractScopeTypes(shopDrawingsEnabled);
            if (legacyScope.every((token) => remaining.has(token))) {
                legacyScope.forEach((token) => remaining.delete(token));
                sources.push(DocumentSource.ContractDocs);
                break;
            }
        }
    }

    if (remaining.delete(DOCUMENTS_CATEGORY_SCOPE)) {
        sources.push(DocumentSource.Documents);
    }

    for (const token of documentFilters) {
        if (!remaining.delete(token)) continue;
        if (token.startsWith(FOLDER_TOKEN_PREFIX) || isAreaToken(token)) {
            sources.push(token as SourceToken);
            continue;
        }
        if (isFilterCategorySource(token) || isWireSource(token)) {
            sources.push(token);
            continue;
        }
        const source = TYPE_TOKEN_TO_SOURCE[token];
        if (!source) return null;
        sources.push(source);
    }
    return sources;
}
